import { Component, OnInit, Output, EventEmitter, Inject, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { DIALOG_DATA } from '../../services/dialog.service';
import { DialogService } from '../../services/dialog.service';
import { LoginError, OTPRequestResponse } from '../../interfaces/auth.interface';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'ufp-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class LoginComponent implements OnInit, AfterViewInit {
  @Output() loginSuccess = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  activeTab: 'signin' | 'signup' = 'signin';
  loginMethod: 'email' | 'phone' = 'email';
  showOtpVerification = false;
  useOtp = false;
  verificationMethod = 'email';
  alertMessage = '';
  otpDigits: string[] = new Array(4).fill('');
  enableSignup: boolean;
  private resendTimer: number = 30;
  canResendOtp: boolean = false;
  resendTimerText: string = '';
  showPassword: boolean = false;
  
  // Preferred auth properties
  preferredAuthMethods: string[] = [];
  isOtpOnlyMode: boolean = false;
  isBasicAuthOnlyMode: boolean = false;

  loginForm!: FormGroup;
  signupForm!: FormGroup;

  // Logo paths - update these with your actual asset paths
  googleLogo = 'https://d1veyxrobexfn7.cloudfront.net/widgets/login/google_logo.png';
  appleLogo = 'https://d1veyxrobexfn7.cloudfront.net/widgets/login/apple_logo_white.png';
  microsoftLogo = 'https://d1veyxrobexfn7.cloudfront.net/widgets/login/microsoft_logo.svg';

  // Add this property to store OTP UUID
  private otpUuid: string = '';

  @ViewChild('emailInput') emailInput!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private dialogService: DialogService,
    @Inject(DIALOG_DATA) private data: { 
      enableSignup: boolean;
      companyUuid: string;
      preferredAuth?: string;
      theme?: Record<string, string>;
      customThemeVariables?: Record<string, string>;
    }
  ) {
    this.enableSignup = data.enableSignup;
    this.initializePreferredAuth();
    this.initializeForms();
  }

  private initializePreferredAuth() {
    if (this.data.preferredAuth) {
      this.preferredAuthMethods = this.data.preferredAuth.split(',').map(method => method.trim().toLowerCase());
      this.isOtpOnlyMode = this.preferredAuthMethods.includes('otp') && this.preferredAuthMethods.length === 1;
      this.isBasicAuthOnlyMode = this.preferredAuthMethods.includes('basic_auth') && this.preferredAuthMethods.length === 1;
      
      // If OTP only mode, automatically enable OTP
      if (this.isOtpOnlyMode) {
        this.useOtp = true;
        // Don't automatically request OTP here, wait for user to enter email/phone and click Send OTP
      }
    }
  }

  private initializeForms() {
    this.loginForm = this.fb.group({
      //TODO: need to seperate validators for prod and non prod. prod validator should not allow use of '+','%' in email address but need this in non prod for testing.
      // email: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]], // use this in prod
      email: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]], // use this in non prod
      phoneNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      countryCode: ['+91', [Validators.required, Validators.pattern(/^\+[0-9]{1,4}$/)]],
      password: ['', Validators.required]
    });

    // Initially disable the unused fields based on login method
    if (this.loginMethod === 'phone') {
      this.loginForm.get('email')?.disable();
    } else {
      this.loginForm.get('phoneNumber')?.disable();
      this.loginForm.get('countryCode')?.disable();
    }

    // Disable password field in OTP only mode
    if (this.isOtpOnlyMode) {
      this.loginForm.get('password')?.disable();
    }

    this.signupForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      repeatPassword: ['', Validators.required]
    }, { validator: this.passwordMatchValidator });
  }

  private passwordMatchValidator(g: FormGroup) {
    return g.get('password')?.value === g.get('repeatPassword')?.value
      ? null : { mismatch: true };
  }

  setLoginMethod(method: 'phone' | 'email') {
    this.loginMethod = method;
    this.alertMessage = '';
    
    // Reset OTP state
    this.showOtpVerification = false;
    this.useOtp = false;
    this.otpDigits = new Array(4).fill('');
    this.otpUuid = '';
    
    // Reset form values
    this.loginForm.patchValue({
      email: '',
      phoneNumber: '',
      countryCode: '+91',
      password: ''
    });

    // Enable/disable fields based on method
    if (method === 'phone') {
      this.loginForm.get('phoneNumber')?.enable();
      this.loginForm.get('countryCode')?.enable();
      this.loginForm.get('email')?.disable();
    } else {
      this.loginForm.get('email')?.enable();
      this.loginForm.get('phoneNumber')?.disable();
      this.loginForm.get('countryCode')?.disable();
    }

    // Re-enable password field (unless in OTP only mode)
    if (!this.isOtpOnlyMode) {
      this.loginForm.get('password')?.enable();
    }
  }

  toggleOtpLogin(): void {
    if (this.loginMethod === 'email' && !this.loginForm.get('email')?.valid) {
      this.alertMessage = 'Please enter a valid email first';
      return;
    }
    if (this.loginMethod === 'phone' && !this.loginForm.get('phoneNumber')?.valid) {
      this.alertMessage = 'Please enter a valid phone number first';
      return;
    }

    this.useOtp = !this.useOtp;
    if (this.useOtp) {
      this.requestOtp();
    } else {
      this.showOtpVerification = false;
      this.loginForm.get('password')?.enable();
      this.alertMessage = '';
    }
  }

  onOtpInput(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value && !/^\d+$/.test(value)) {
      input.value = '';
      return;
    }

    this.otpDigits[index] = value;

    if (event.key !== 'Backspace') {
      if (value && index < 3) {
        const nextInput = document.querySelector(`[data-index="${index + 1}"]`) as HTMLInputElement;
        if (nextInput) nextInput.focus();
      }
    } else if (index > 0) {
      const prevInput = document.querySelector(`[data-index="${index - 1}"]`) as HTMLInputElement;
      if (prevInput) prevInput.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const paste = event.clipboardData?.getData('text');
    const digits = paste?.match(/\d/g);
    
    if (digits) {
      this.otpDigits = [...digits.slice(0, 4), ...new Array(4 - digits.length).fill('')];
      
      const nextEmptyIndex = this.otpDigits.findIndex(d => !d);
      const targetIndex = nextEmptyIndex === -1 ? 3 : nextEmptyIndex;
      const targetInput = document.querySelector(`[data-index="${targetIndex}"]`) as HTMLInputElement;
      if (targetInput) targetInput.focus();
    }
  }

  requestNewOtp(event: Event): void {
    event.preventDefault();
    if (!this.canResendOtp) return;
    
    this.requestOtp();
    this.otpDigits = new Array(4).fill('');
    const firstInput = document.querySelector('[data-index="0"]') as HTMLInputElement;
    if (firstInput) firstInput.focus();
  }

  ngOnInit(): void {
    if (!this.enableSignup) {
      this.activeTab = 'signin';
    }
  }

  setActiveTab(tab: 'signin' | 'signup'): void {
    if (tab === 'signup' && !this.enableSignup) {
      return;
    }
    this.activeTab = tab;
    this.showOtpVerification = false;
    this.useOtp = false;
    this.alertMessage = '';
  }

  onLogin(): void {
    // Mark all fields as touched to trigger validation display
    Object.keys(this.loginForm.controls).forEach(key => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });

    if (!this.loginForm.valid) {
      if (this.loginMethod === 'email' && this.loginForm.get('email')?.invalid) {
        this.alertMessage = 'Please enter a valid email address';
      } else if (this.loginMethod === 'phone' && this.loginForm.get('phoneNumber')?.invalid) {
        this.alertMessage = 'Please enter a valid phone number';
      }
      return;
    }

    const formValues = this.loginForm.value;
    let identifier: string;
    let countryCode: string | undefined;

    if (this.loginMethod === 'phone') {
      identifier = formValues.phoneNumber;
      countryCode = formValues.countryCode;
      
      this.authService.verifyCredentials(identifier, formValues.password, this.data.companyUuid, countryCode)
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.loginSuccess.emit();
              this.dialogService.closeDialog();
            } else {
              this.alertMessage = response.errors?.[0] || 'Login failed';
            }
          },
          error: (error: { error: LoginError }) => {
            if (error.error?.errorMessage) {
              switch (error.error.errorCode) {
                case 'wrong':
                  this.alertMessage = 'Incorrect password. Please try again.';
                  break;
                case 'not_found':
                  this.alertMessage = 'Account not found. Please check your phone number.';
                  break;
                default:
                  this.alertMessage = error.error.errorMessage;
              }
            } else {
              this.alertMessage = 'An error occurred during login. Please try again.';
            }
            console.error('Login error:', error);
          }
        });
    } else {
      // Email login
      identifier = formValues.email;
      this.authService.verifyCredentials(identifier, formValues.password, this.data.companyUuid)
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.loginSuccess.emit();
              this.dialogService.closeDialog();
            } else {
              this.alertMessage = response.errors?.[0] || 'Login failed';
            }
          },
          error: (error: { error: LoginError }) => {
            if (error.error?.errorMessage) {
              switch (error.error.errorCode) {
                case 'wrong':
                  this.alertMessage = 'Incorrect password. Please try again.';
                  break;
                case 'not_found':
                  this.alertMessage = 'Account not found. Please check your email.';
                  break;
                default:
                  this.alertMessage = error.error.errorMessage;
              }
            } else {
              this.alertMessage = 'An error occurred during login. Please try again.';
            }
            console.error('Login error:', error);
          }
        });
    }
  }

  onSignup(): void {
    // Implement signup logic
  }

  verifyOtp(): void {
    const otp = this.otpDigits.join('');
    if (!otp || otp.length !== 4 || !this.otpUuid) {
      this.alertMessage = 'Please enter a valid OTP';
      return;
    }

    const formValues = this.loginForm.value;
    const request: any = {
      company_uuid: this.data.companyUuid,
      email_id: '',
      mobile_country_code: '',
      mobile_number: 0,
      password: '',
      otp_uuid: this.otpUuid,
      otp_code: parseInt(otp, 10)
    };

    if (this.loginMethod === 'email') {
      request.email_id = formValues.email;
    } else {
      request.mobile_country_code = formValues.countryCode;
      request.mobile_number = parseInt(formValues.phoneNumber, 10);
    }

    this.authService.verifyOTP(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.loginSuccess.emit();
          this.dialogService.closeDialog();
        } else {
          this.alertMessage = response.errors?.[0] || 'OTP verification failed';
        }
      },
      error: (error) => {
        this.alertMessage = error.error?.errorMessage || 'Failed to verify OTP. Please try again.';
        console.error('OTP verification error:', error);
      }
    });
  }

  closeModalHandler(): void {
    this.dialogService.closeDialog();
  }

  @HostListener('document:keydown.escape')
  onEscapePress() {
    this.closeModalHandler();
  }

  // Social login methods
  loginWithGoogle(): void {
    // Implement Google login
  }

  loginWithApple(): void {
    // Implement Apple login
  }

  loginWithMicrosoft(): void {
    // Implement Microsoft login
  }

  signupWithGoogle(): void {
    // Implement Google signup
  }

  signupWithApple(): void {
    // Implement Apple signup
  }

  signupWithMicrosoft(): void {
    // Implement Microsoft signup
  }

  private startResendTimer(): void {
    this.resendTimer = 30;
    this.canResendOtp = false;
    
    const timer = setInterval(() => {
      this.resendTimer--;
      this.resendTimerText = `Resend OTP in ${this.resendTimer}s`;
      
      if (this.resendTimer <= 0) {
        clearInterval(timer);
        this.canResendOtp = true;
        this.resendTimerText = '';
      }
    }, 1000);
  }

  private requestOtp(): void {
    const formValues = this.loginForm.value;
    const request: any = {
      company_uuid: this.data.companyUuid,
      email_id: '',
      mobile_country_code: '',
      mobile_number: 0
    };

    if (this.loginMethod === 'email') {
      request.email_id = formValues.email;
    } else {
      request.mobile_country_code = formValues.countryCode;
      request.mobile_number = parseInt(formValues.phoneNumber, 10);
    }

    this.authService.requestOTP(request).subscribe({
      next: (response) => {
        if (response.id) {
          this.otpUuid = response.id;
          this.showOtpVerification = true;
          this.loginForm.get('password')?.disable();
          
          this.verificationMethod = this.loginMethod;
          
          const maskedValue = this.loginMethod === 'email'
            ? formValues.email.replace(/(.{2})(.*)(?=@)/, (_: string, start: string, rest: string) => start + '*'.repeat(rest.length))
            : formValues.phoneNumber.slice(0, -4).replace(/\d/g, '*') + formValues.phoneNumber.slice(-4);
          
          this.alertMessage = `OTP has been sent to your ${this.verificationMethod}: ${maskedValue}`;
          this.startResendTimer();
        } else {
          this.alertMessage = 'Failed to send OTP';
        }
      },
      error: (error) => {
        this.alertMessage = 'Failed to send OTP. Please try again.';
        console.error('OTP request error:', error);
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  requestOtpDirectly(): void {
    // Validate the form first
    if (this.loginMethod === 'email' && !this.loginForm.get('email')?.valid) {
      this.alertMessage = 'Please enter a valid email first';
      return;
    }
    if (this.loginMethod === 'phone' && !this.loginForm.get('phoneNumber')?.valid) {
      this.alertMessage = 'Please enter a valid phone number first';
      return;
    }

    this.requestOtp();
  }

  ngAfterViewInit() {
    // Set focus on email input after view is initialized
    setTimeout(() => {
      this.emailInput?.nativeElement?.focus();
    });
  }
}
